import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X } from 'lucide-react';
import { getApiBaseUrl } from '../../utils/apiConfig.js';

const AdminDistributors = () => {
  const navigate = useNavigate();
  const API = getApiBaseUrl();

  // 状态管理
  const [distributors, setDistributors] = useState([]);
  const [activeTab, setActiveTab] = useState('pending'); // pending/active/rejected
  const [selectedDist, setSelectedDist] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // 审核表单
  const [commissionRate, setCommissionRate] = useState('500');
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);

  // 分页
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  // 权限检查
  useEffect(() => {
    const checkAuthority = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (!res.ok) {
          throw new Error('未授权');
        }
        const userData = await res.json();
        if (userData.role !== 'admin') {
          setError('您没有权限访问此页面（需要管理员权限）');
          setIsAuthorized(false);
          setTimeout(() => navigate('/home'), 2000);
        } else {
          setIsAuthorized(true);
        }
      } catch (err) {
        setError('权限验证失败，请重新登录');
        setTimeout(() => navigate('/auth/login'), 2000);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuthority();
  }, [API, navigate]);

  // 加载分销商列表
  const fetchDistributors = useCallback(
    async (status = 'pending', skip = 0) => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          `${API}/commission/admin/distributors?status=${status}&skip=${skip}&limit=${LIMIT}`,
          { credentials: 'include' }
        );
        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.detail || '获取失败');
        }
        const data = await res.json();
        setDistributors(data.items || []);
        setTotal(data.total || 0);
      } catch (err) {
        setError(err.message || '加载失败');
        setDistributors([]);
      } finally {
        setLoading(false);
      }
    },
    [API]
  );

  // 初始加载（仅在授权后加载）
  useEffect(() => {
    if (isAuthorized && !authLoading) {
      fetchDistributors('pending', 0);
    }
  }, [isAuthorized, authLoading, fetchDistributors]);

  // 批准分销商
  const handleApprove = async () => {
    if (!selectedDist) return;
    setApproving(true);
    try {
      const res = await fetch(`${API}/commission/admin/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_id: selectedDist.user_id,
          approved: true,
          commission_rate: parseInt(commissionRate),
        }),
      });
      if (!res.ok) {
        throw new Error(await res.json().then(d => d.detail || '批准失败'));
      }
      setIsModalOpen(false);
      setCommissionRate('500');
      fetchDistributors(activeTab, page * LIMIT);
    } catch (err) {
      alert(err.message);
    } finally {
      setApproving(false);
    }
  };

  // 拒绝分销商
  const handleReject = async () => {
    if (!selectedDist) return;
    if (!rejectReason.trim()) {
      alert('请填写拒绝原因');
      return;
    }
    setApproving(true);
    try {
      const res = await fetch(`${API}/commission/admin/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_id: selectedDist.user_id,
          approved: false,
          reject_reason: rejectReason,
        }),
      });
      if (!res.ok) {
        throw new Error(await res.json().then(d => d.detail || '拒绝失败'));
      }
      setIsModalOpen(false);
      setRejectReason('');
      setCommissionRate('500');
      fetchDistributors(activeTab, page * LIMIT);
    } catch (err) {
      alert(err.message);
    } finally {
      setApproving(false);
    }
  };

  // 打开审核模态框
  const openModal = (dist) => {
    setSelectedDist(dist);
    setCommissionRate('500');
    setRejectReason('');
    setIsModalOpen(true);
  };

  // 状态样式映射
  const getStatusStyle = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  // 状态文本映射
  const getStatusText = (status) => {
    const texts = {
      pending: '⏳ 待审核',
      active: '✅ 已激活',
      rejected: '❌ 已拒绝',
    };
    return texts[status] || status;
  };

  // 日期格式化
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return '今天';
    if (diffDays === 1) return '昨天';
    if (diffDays < 7) return `${diffDays}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  // 切换标签页
  const switchTab = (newTab) => {
    setActiveTab(newTab);
    setPage(0);
    fetchDistributors(newTab, 0);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* 权限检查加载状态 */}
        {authLoading && (
          <div className="p-8 text-center">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-gray-600">验证权限中...</p>
            </div>
          </div>
        )}

        {/* 权限不足提示 */}
        {!authLoading && !isAuthorized && (
          <div className="p-8 text-center bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 font-semibold">{error}</p>
            <p className="text-red-600 text-sm mt-2">即将跳转，请稍候...</p>
          </div>
        )}

        {/* 主内容区（仅授权用户显示） */}
        {!authLoading && isAuthorized && (
          <>
        {/* 标题栏 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <button
              onClick={() => navigate('/home')}
              className="inline-flex items-center px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回个人中心
            </button>
            <h1 className="text-2xl font-bold text-gray-800">📊 分销商管理后台</h1>
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* 状态标签切换 */}
        <div className="flex space-x-2 mb-6">
          {['pending', 'active', 'rejected'].map((tab) => (
            <button
              key={tab}
              onClick={() => switchTab(tab)}
              className={`px-4 py-2 rounded font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab === 'pending'
                ? '⏳ 待审核'
                : tab === 'active'
                ? '✅ 已激活'
                : '❌ 已拒绝'}
            </button>
          ))}
        </div>

        {/* 表格 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">加载中...</div>
          ) : distributors.length === 0 ? (
            <div className="p-8 text-center text-gray-500">暂无数据</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-100 text-left text-sm">
                    <th className="px-4 py-3 font-semibold text-gray-700">用户名</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">邀请码</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">联系人</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">公司名称</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">申请时间</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">状态</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {distributors.map((dist) => (
                    <tr key={dist.user_id} className="border-t text-sm hover:bg-gray-50">
                      <td className="px-4 py-3">{dist.username}</td>
                      <td className="px-4 py-3 font-mono text-xs bg-gray-50 rounded p-1">
                        {dist.invitation_code}
                      </td>
                      <td className="px-4 py-3">{dist.contact_person}</td>
                      <td className="px-4 py-3">{dist.company_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(dist.applied_at)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getStatusStyle(
                            dist.status
                          )}`}
                        >
                          {getStatusText(dist.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openModal(dist)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors"
                        >
                          {activeTab === 'pending' ? '审核' : '查看'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 分页 */}
        <div className="flex justify-between items-center mt-6 text-sm">
          <div className="text-gray-600">共 {total} 条记录</div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setPage((p) => p - 1);
                fetchDistributors(activeTab, (page - 1) * LIMIT);
              }}
              disabled={page === 0}
              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 rounded transition-colors"
            >
              上一页
            </button>
            <span className="px-3 py-1 text-gray-700">第 {page + 1} 页</span>
            <button
              onClick={() => {
                setPage((p) => p + 1);
                fetchDistributors(activeTab, (page + 1) * LIMIT);
              }}
              disabled={(page + 1) * LIMIT >= total}
              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 rounded transition-colors"
            >
              下一页
            </button>
          </div>
        </div>
        </>
        )}

      {/* 审核模态框 */}
      {isModalOpen && selectedDist && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6 max-h-[90vh] overflow-y-auto">
            {/* 标题栏 */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                {activeTab === 'pending' ? '📋 审核分销商申请' : '📊 分销商详情'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* 基本信息 */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600 font-medium">用户名:</span>{' '}
                  <span className="ml-2">{selectedDist.username}</span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">邀请码:</span>{' '}
                  <code className="ml-2 bg-white px-2 py-1 rounded font-mono text-xs">
                    {selectedDist.invitation_code}
                  </code>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">申请时间:</span>{' '}
                  <span className="ml-2">{formatDateTime(selectedDist.applied_at)}</span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">当前状态:</span>{' '}
                  <span className="ml-2 font-medium">{getStatusText(selectedDist.status)}</span>
                </div>
              </div>
            </div>

            {/* 申请信息 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-800 mb-3">💼 申请信息</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600 font-medium">公司名称:</span>{' '}
                  <span className="ml-2">{selectedDist.company_name || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">联系人:</span>{' '}
                  <span className="ml-2">{selectedDist.contact_person}</span>
                </div>
                <div>
                  <span className="text-gray-600 font-medium">营业执照:</span>{' '}
                  <span className="ml-2">{selectedDist.business_license || '-'}</span>
                </div>
              </div>
            </div>

            {/* 统计数据 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-800 mb-3">📊 数据统计</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center bg-blue-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedDist.total_customers}
                  </div>
                  <div className="text-gray-600 text-xs mt-1">累计客户</div>
                </div>
                <div className="text-center bg-green-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-600">
                    {selectedDist.total_orders}
                  </div>
                  <div className="text-gray-600 text-xs mt-1">累计订单</div>
                </div>
                <div className="text-center bg-purple-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-purple-600">
                    ¥{(selectedDist.total_commission / 100).toFixed(2)}
                  </div>
                  <div className="text-gray-600 text-xs mt-1">累计佣金</div>
                </div>
              </div>
            </div>

            {/* 审核操作区（仅待审核状态显示） */}
            {activeTab === 'pending' && (
              <>
                {/* 批准区域 */}
                <div className="border border-green-200 rounded-lg p-4 mb-3 bg-green-50">
                  <h3 className="font-semibold text-green-800 mb-3">✅ 批准申请</h3>
                  <div className="mb-3">
                    <label className="block text-sm text-gray-700 font-medium mb-1">
                      佣金设置（分/单）
                    </label>
                    <input
                      type="number"
                      value={commissionRate}
                      onChange={(e) => setCommissionRate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="500"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      当前设置: {(parseInt(commissionRate) / 100).toFixed(2)}元/单
                    </p>
                  </div>
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white py-2 rounded-lg font-medium transition-colors"
                  >
                    {approving ? '处理中...' : '批准'}
                  </button>
                </div>

                {/* 拒绝区域 */}
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <h3 className="font-semibold text-red-800 mb-3">❌ 拒绝申请</h3>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows="3"
                    placeholder="请填写拒绝原因（必填）"
                  />
                  <button
                    onClick={handleReject}
                    disabled={approving}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white py-2 rounded-lg font-medium transition-colors"
                  >
                    {approving ? '处理中...' : '拒绝'}
                  </button>
                </div>
              </>
            )}

            {/* 非待审核状态显示审核结果 */}
            {activeTab !== 'pending' && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="text-sm space-y-2">
                  {selectedDist.approved_at && (
                    <div>
                      <span className="text-gray-600 font-medium">审核时间:</span>{' '}
                      <span className="ml-2">{formatDateTime(selectedDist.approved_at)}</span>
                    </div>
                  )}
                  {activeTab === 'active' && (
                    <div>
                      <span className="text-gray-600 font-medium">佣金比例:</span>{' '}
                      <span className="ml-2">
                        {(selectedDist.commission_rate / 100).toFixed(2)}元/单
                      </span>
                    </div>
                  )}
                  {activeTab === 'rejected' && selectedDist.reject_reason && (
                    <div>
                      <span className="text-gray-600 font-medium">拒绝原因:</span>{' '}
                      <span className="ml-2">{selectedDist.reject_reason}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 关闭按钮 */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="w-full mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              关闭
            </button>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default AdminDistributors;